import { mergeQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import { createAdminMutations, createAdminQueries } from './domains/admin';
import { createAlertMutations, createAlertQueries } from './domains/alerts';
import { createAdminAnalyticsQueries } from './domains/admin-analytics';
import { createAiSummaryMutations, createAiSummaryQueries } from './domains/ai-summary';
import { createAnalystQueries } from './domains/analyst';
import { createAppQueries } from './domains/app';
import { createAuthMutations, createAuthQueries } from './domains/auth';
import { createBillingMutations, createBillingQueries } from './domains/billing';
import { createBugReportsMutations, createBugReportsQueries } from './domains/bug-reports';
import { createCalendarQueries } from './domains/calendar';
import { createCampaignMutations, createCampaignQueries } from './domains/campaigns';
import {
  createChartingPresetMutations,
  createChartingPresetQueries,
} from './domains/charting-presets';
import { createChatbotMutations, createChatbotQueries } from './domains/chatbot';
import {
  createChatbotArtifactsMutations,
  createChatbotArtifactsQueries,
} from './domains/chatbot-artifacts';
import { createChatbotBoardsMutations, createChatbotBoardsQueries } from './domains/chatbot-boards';
import {
  createCompanyProfileMutations,
  createCompanyProfileQueries,
} from './domains/company-profiles';
import { createComparisonMutations } from './domains/comparison';
import {
  createComparisonGroupQueries,
  createComparisonGroupMutations,
} from './domains/comparison-groups';
import { createConversationsMutations, createConversationsQueries } from './domains/conversations';
import {
  createCopilotSettingsMutations,
  createCopilotSettingsQueries,
} from './domains/copilot-settings';
import { createContactMutations } from './domains/contact';
import { createExchangeRateQueries } from './domains/exchange-rates';
import { createFilingsQueries } from './domains/filings';
import { createFinnhubQueries } from './domains/finnhub';
import { createFirmQueries } from './domains/firm';
import { createHealthQueries } from './domains/health';
import { createImageQueries } from './domains/images';
import { createInstrumentMutations, createInstrumentQueries } from './domains/instruments';
import { createInternalMutations, createInternalQueries } from './domains/internal';
import { createInvestorRelationsQueries } from './domains/investor-relations';
import {
  createMergedPortfolioMutations,
  createMergedPortfolioQueries,
} from './domains/merged-portfolios';
import { createMetricPresetMutations, createMetricPresetQueries } from './domains/metric-presets';
import { createNewsMutations, createNewsQueries } from './domains/news';
import { createNewsletterMutations } from './domains/newsletter';
import { createNotificationMutations, createNotificationQueries } from './domains/notifications';
import { createPeerQueries } from './domains/peers';
import { createPortfolioMutations, createPortfolioQueries } from './domains/portfolios';
import { createPositionMutations } from './domains/positions';
import { createProjectionMutations, createProjectionQueries } from './domains/projections';
import { createFollowingQueries } from './domains/following';
import { createPublicMutations, createPublicQueries } from './domains/public';
import { createResearchOverviewQueries } from './domains/research-overview';
import { createResearchReportQueries } from './domains/research-reports';
import { createScoringQueries } from './domains/scoring';
import { createScreenerMutations, createScreenerQueries } from './domains/screener';
import { createSnaptradeMutations, createSnaptradeQueries } from './domains/snaptrade';
import { createUserMutations, createUserQueries } from './domains/users';
import { createWalletMutations, createWalletQueries } from './domains/wallets';
import { createWatchlistMutations, createWatchlistQueries } from './domains/watchlists';
import { createWebhookMutations } from './domains/webhooks';

/**
 * Every domain's query keys, merged into one factory. This and {@link createDomainMutations} are
 * their own functions because one literal of both lists measured 83 lines, over CodeQL's 80-line
 * god-function band, and every new domain adds a line to each.
 *
 * THIS LIST IS AT ITS CEILING: `mergeQueryKeys` types its result by recursing once per domain, and
 * a 46th domain failed every consumer's type-check with TS2589 ("type instantiation is excessively
 * deep", CI run 36329431688, 2026-09-27). Host a new query in the closest existing domain, as
 * `instruments.treasurySeries` is, or split this merge before adding one.
 */
function createDomainQueries(client: StoxlyClient) {
  return mergeQueryKeys(
    createAdminQueries(client),
    createAdminAnalyticsQueries(client),
    createAiSummaryQueries(client),
    createAlertQueries(client),
    createAnalystQueries(client),
    createAppQueries(client),
    createAuthQueries(client),
    createBillingQueries(client),
    createBugReportsQueries(client),
    createCalendarQueries(client),
    createCampaignQueries(client),
    createChartingPresetQueries(client),
    createComparisonGroupQueries(client),
    createChatbotQueries(client),
    createChatbotArtifactsQueries(client),
    createChatbotBoardsQueries(client),
    createCompanyProfileQueries(client),
    createConversationsQueries(client),
    createCopilotSettingsQueries(client),
    createExchangeRateQueries(client),
    createFilingsQueries(client),
    createFinnhubQueries(client),
    createFirmQueries(client),
    createFollowingQueries(client),
    createHealthQueries(client),
    createImageQueries(client),
    createInstrumentQueries(client),
    createInternalQueries(client),
    createInvestorRelationsQueries(client),
    createMergedPortfolioQueries(client),
    createMetricPresetQueries(client),
    createNewsQueries(client),
    createNotificationQueries(client),
    createPeerQueries(client),
    createPortfolioQueries(client),
    createProjectionQueries(client),
    createPublicQueries(client),
    createResearchOverviewQueries(client),
    createResearchReportQueries(client),
    createScoringQueries(client),
    createScreenerQueries(client),
    createSnaptradeQueries(client),
    createUserQueries(client),
    createWalletQueries(client),
    createWatchlistQueries(client),
  );
}

/** Every domain's mutations, namespaced by domain (`mutations.wallets.create`). */
function createDomainMutations(client: StoxlyClient) {
  return {
    admin: createAdminMutations(client),
    alerts: createAlertMutations(client),
    aiSummary: createAiSummaryMutations(client),
    auth: createAuthMutations(client),
    billing: createBillingMutations(client),
    bugReports: createBugReportsMutations(client),
    campaigns: createCampaignMutations(client),
    chartingPresets: createChartingPresetMutations(client),
    chatbot: createChatbotMutations(client),
    chatbotArtifacts: createChatbotArtifactsMutations(client),
    chatbotBoards: createChatbotBoardsMutations(client),
    companyProfiles: createCompanyProfileMutations(client),
    comparison: createComparisonMutations(client),
    comparisonGroups: createComparisonGroupMutations(client),
    conversations: createConversationsMutations(client),
    copilotSettings: createCopilotSettingsMutations(client),
    contact: createContactMutations(client),
    instruments: createInstrumentMutations(client),
    internal: createInternalMutations(client),
    mergedPortfolios: createMergedPortfolioMutations(client),
    metricPresets: createMetricPresetMutations(client),
    news: createNewsMutations(client),
    newsletter: createNewsletterMutations(client),
    notifications: createNotificationMutations(client),
    portfolios: createPortfolioMutations(client),
    positions: createPositionMutations(client),
    projections: createProjectionMutations(client),
    public: createPublicMutations(client),
    screener: createScreenerMutations(client),
    snaptrade: createSnaptradeMutations(client),
    users: createUserMutations(client),
    wallets: createWalletMutations(client),
    watchlists: createWatchlistMutations(client),
    webhooks: createWebhookMutations(client),
  };
}

/**
 * Builds every Stoxly TanStack Query definition bound to a client instance.
 *
 * Framework-agnostic: entries are plain `{ queryKey, queryFn }` /
 * `{ mutationFn }` objects, accepted as-is by `useQuery`/`useMutation` in
 * @tanstack/vue-query and @tanstack/react-query, and by
 * `QueryClient.fetchQuery` from @tanstack/query-core.
 *
 * @example
 * const client = createStoxlyClient({ baseUrl, getToken });
 * const stoxly = createStoxlyQueries(client);
 * useQuery(stoxly.queries.wallets.detail(id));                  // vue-query / react-query
 * useQuery({ ...stoxly.queries.wallets.all, staleTime: 30_000 }); // with overrides
 * useMutation(stoxly.mutations.wallets.create);
 */
export function createStoxlyQueries(client: StoxlyClient) {
  return {
    queries: createDomainQueries(client),
    mutations: createDomainMutations(client),
  };
}

export type StoxlyQueries = ReturnType<typeof createStoxlyQueries>;
